/* Impact × Effort Prioritisation — 2×2 idea triage matrix */
import ToolWizard from "./ToolWizard";
import { Plus, Trash2, FileText } from "lucide-react";

const QUADRANTS = [
  { id: "quick_wins",  label: "Quick Wins",  desc: "High impact · Low effort — Do first", color: "#16A34A" },
  { id: "big_bets",    label: "Big Bets",    desc: "High impact · High effort — Plan & resource", color: "#3B82F6" },
  { id: "fill_ins",    label: "Fill-ins",    desc: "Low impact · Low effort — Delegate", color: "#A855F7" },
  { id: "time_sinks",  label: "Time Sinks",  desc: "Low impact · High effort — Reconsider", color: "#DC2626" },
];

function placeIdea(impact, effort) {
  const hi = impact >= 3, he = effort >= 3;
  if (hi && !he) return "quick_wins";
  if (hi && he)  return "big_bets";
  if (!hi && !he) return "fill_ins";
  return "time_sinks";
}

export default function ImpactEffortWizard({ project, onSave, onExportPDF }) {
  const steps = [
    {
      key: "setup", label: "Setup",
      render: ({ data, updateData }) => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ie-setup">
          <div className="md:col-span-2"><label className="block text-xs font-semibold text-sage-600 mb-1">Theme / brainstorm topic</label><input value={data.theme || ""} onChange={e => updateData({ theme: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" placeholder="e.g. Reduce on-boarding cycle time" /></div>
          <div><label className="block text-xs font-semibold text-sage-600 mb-1">Facilitator</label><input value={data.facilitator || ""} onChange={e => updateData({ facilitator: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" /></div>
          <div><label className="block text-xs font-semibold text-sage-600 mb-1">Session date</label><input type="date" value={data.session_date || ""} onChange={e => updateData({ session_date: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" /></div>
          <div className="md:col-span-2"><label className="block text-xs font-semibold text-sage-600 mb-1">Participants</label><input value={data.participants || ""} onChange={e => updateData({ participants: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" placeholder="Comma-separated names" /></div>
        </div>
      ),
    },
    {
      key: "ideas", label: "Capture Ideas",
      render: ({ data, updateData }) => {
        const items = data.ideas || [];
        const add = () => updateData({ ideas: [...items, { title: "", impact: 3, effort: 3, owner: "", note: "" }] });
        const upd = (i, p) => updateData({ ideas: items.map((m, idx) => idx === i ? { ...m, ...p } : m) });
        const rm  = (i) => updateData({ ideas: items.filter((_, idx) => idx !== i) });
        return (
          <div className="space-y-2" data-testid="ie-ideas">
            <div className="bg-sage-50 rounded-md p-2 text-xs text-sage-600">Score each idea on Impact (1–5) and Effort (1–5). The 2×2 chart on Diagnostic auto-places them into Quick Wins, Big Bets, Fill-ins, or Time Sinks.</div>
            {items.map((it, i) => {
              const q = placeIdea(it.impact, it.effort);
              const qm = QUADRANTS.find(qq => qq.id === q);
              return (
                <div key={i} className="bg-white border border-sage-200 rounded-md p-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-center" data-testid={`ie-idea-${i}`}>
                  <input data-testid={`ie-idea-${i}-title`} placeholder="Idea" value={it.title} onChange={e => upd(i, { title: e.target.value })} className="md:col-span-4 border border-sage-200 rounded-md px-2 py-1.5 text-sm" />
                  <div className="md:col-span-2"><label className="text-[10px] text-sage-500">Impact ({it.impact})</label><input data-testid={`ie-idea-${i}-impact`} type="range" min="1" max="5" value={it.impact} onChange={e => upd(i, { impact: Number(e.target.value) })} className="w-full" /></div>
                  <div className="md:col-span-2"><label className="text-[10px] text-sage-500">Effort ({it.effort})</label><input data-testid={`ie-idea-${i}-effort`} type="range" min="1" max="5" value={it.effort} onChange={e => upd(i, { effort: Number(e.target.value) })} className="w-full" /></div>
                  <input placeholder="Owner" value={it.owner || ""} onChange={e => upd(i, { owner: e.target.value })} className="md:col-span-2 border border-sage-200 rounded-md px-2 py-1.5 text-xs" />
                  <span className="md:col-span-1 rounded-full px-2 py-0.5 text-white text-[10px] font-bold text-center" style={{ backgroundColor: qm.color }}>{qm.label}</span>
                  <button onClick={() => rm(i)} className="text-red-500 justify-self-end"><Trash2 className="w-4 h-4" /></button>
                  <input placeholder="Note (optional)" value={it.note || ""} onChange={e => upd(i, { note: e.target.value })} className="md:col-span-12 border border-sage-100 rounded-md px-2 py-1 text-xs text-sage-600" />
                </div>
              );
            })}
            <button data-testid="ie-add-idea" onClick={add} className="flex items-center gap-1 border border-dashed border-sage-300 text-sage-600 rounded-md px-3 py-2 text-sm hover:bg-sage-50 w-full justify-center"><Plus className="w-3.5 h-3.5" /> Add idea</button>
          </div>
        );
      },
    },
    {
      key: "diagnostic", label: "2×2 Matrix",
      render: ({ data }) => {
        const ideas = (data.ideas || []).map(it => ({ ...it, _q: placeIdea(it.impact, it.effort) }));
        const grouped = QUADRANTS.map(q => ({ ...q, items: ideas.filter(i => i._q === q.id) }));
        // Position scatter for SVG: x = effort 1-5, y = 6 - impact (so high impact at top)
        const W = 600, H = 420, P = 50;
        const sx = (e) => P + ((Number(e || 1) - 1) / 4) * (W - P * 2);
        const sy = (i) => P + ((5 - Number(i || 1)) / 4) * (H - P * 2);
        const midX = sx(3), midY = sy(3);
        return (
          <div className="space-y-4" data-testid="ie-diagnostic">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {grouped.map(q => (
                <div key={q.id} className="rounded-md p-3 border-2" style={{ borderColor: q.color }} data-testid={`ie-quad-${q.id}`}>
                  <p className="text-xs font-bold uppercase tracking-wider" style={{ color: q.color }}>{q.label}</p>
                  <p className="text-[10px] text-sage-500">{q.desc}</p>
                  <p className="font-heading text-2xl font-bold text-sage-900 mt-1">{q.items.length}</p>
                </div>
              ))}
            </div>
            <div className="bg-white border border-sage-200 rounded-md p-3" data-testid="ie-scatter">
              <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
                <rect x={P} y={P} width={midX - P} height={midY - P} fill="#16A34A0F" />
                <rect x={midX} y={P} width={W - P - midX} height={midY - P} fill="#3B82F60F" />
                <rect x={P} y={midY} width={midX - P} height={H - P - midY} fill="#A855F70F" />
                <rect x={midX} y={midY} width={W - P - midX} height={H - P - midY} fill="#DC26260F" />
                <text x={(P + midX) / 2} y={P + 14} textAnchor="middle" fontSize="11" fill="#16A34A" fontWeight="bold">Quick Wins</text>
                <text x={(midX + W - P) / 2} y={P + 14} textAnchor="middle" fontSize="11" fill="#3B82F6" fontWeight="bold">Big Bets</text>
                <text x={(P + midX) / 2} y={H - P - 6} textAnchor="middle" fontSize="11" fill="#A855F7" fontWeight="bold">Fill-ins</text>
                <text x={(midX + W - P) / 2} y={H - P - 6} textAnchor="middle" fontSize="11" fill="#DC2626" fontWeight="bold">Time Sinks</text>
                <line x1={midX} y1={P} x2={midX} y2={H - P} stroke="#94A3A0" strokeDasharray="3,3" />
                <line x1={P} y1={midY} x2={W - P} y2={midY} stroke="#94A3A0" strokeDasharray="3,3" />
                <line x1={P} y1={H - P} x2={W - P} y2={H - P} stroke="#475569" />
                <line x1={P} y1={P} x2={P} y2={H - P} stroke="#475569" />
                <text x={W / 2} y={H - 8} textAnchor="middle" fontSize="10" fill="#475569">Effort →</text>
                <text x={14} y={H / 2} textAnchor="middle" fontSize="10" fill="#475569" transform={`rotate(-90 14 ${H / 2})`}>Impact →</text>
                {ideas.map((it, idx) => {
                  const c = QUADRANTS.find(q => q.id === it._q).color;
                  return (
                    <g key={idx}>
                      <circle cx={sx(it.effort)} cy={sy(it.impact)} r="11" fill={c} stroke="white" strokeWidth="2" />
                      <text x={sx(it.effort)} y={sy(it.impact) + 3.5} textAnchor="middle" fontSize="10" fontWeight="bold" fill="white">{idx + 1}</text>
                      <text x={sx(it.effort) + 16} y={sy(it.impact) + 3.5} fontSize="10" fill="#1E293B">{(it.title || "").slice(0, 22)}</text>
                    </g>
                  );
                })}
              </svg>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {grouped.map(q => (
                <div key={q.id} className="border-l-4 rounded-md p-3 bg-white" style={{ borderLeftColor: q.color }}>
                  <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: q.color }}>{q.label} ({q.items.length})</p>
                  <ul className="space-y-1">{q.items.map((it, idx) => (
                    <li key={idx} className="text-xs flex items-center gap-2 bg-sage-50 rounded p-1.5">
                      <span className="font-semibold text-sage-900 flex-1">{it.title || "—"}</span>
                      <span className="font-mono text-sage-500">I{it.impact} · E{it.effort}</span>
                      {it.owner && <span className="text-sage-600">{it.owner}</span>}
                    </li>
                  ))}{q.items.length === 0 && <li className="text-xs text-sage-400 italic">—</li>}</ul>
                </div>
              ))}
            </div>
            <button data-testid="export-ie-pdf" onClick={() => onExportPDF(data)} className="bg-sage-600 text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-sage-700 flex items-center gap-2"><FileText className="w-4 h-4" /> Export PDF</button>
          </div>
        );
      },
    },
  ];
  return <ToolWizard project={project} steps={steps} onSave={onSave} completedLabel="Finish 2×2" />;
}

export { placeIdea, QUADRANTS };
